import { RequirePermission } from "@components/common/require-permission";
import { useErrorHandler } from "@components/error-boundary/error-handling-context";
import { TextWithTooltip } from "@components/common/text-with-tooltip";
import { BaseMemberProps } from "@custom-types/base-types";
import {
  Switch,
  useToast,
  Button,
  Dialog,
  NotificationDialog,
  TextField,
  Text,
} from "@flat-ui";
import { isValidEmail } from "@pages/create-project/create-project-helper";
import { memberStateSelector } from "@store/member/member-slice";
import { updateMember } from "@store/member/member-thunks";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { getMemberDisplayName } from "@utils/data-display";
import { MemberEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useState } from "react";

/** A component for editing member details */
export function EditMember({ member }: BaseMemberProps): React.JSX.Element {
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [firstName, setFirstName] = useState<string>(member.firstName ?? "");
  const [lastName, setLastName] = useState<string>(member.lastName ?? "");
  const [email, setEmail] = useState<string>(member.email);
  const [isEmailVerified, setIsEmailVerified] = useState<boolean>(
    member.emailVerified
  );
  const [shouldShowEmailChangeDialog, setShouldShowEmailChangeDialog] =
    useState<boolean>(false);

  const { trackEvent } = useTrackEvent();
  const { handleErrorWithToast } = useErrorHandler();
  const { openToast } = useToast();
  const dispatch = useAppDispatch();

  const { fetchingMember } = useAppSelector(memberStateSelector);

  function onCloseDialog(): void {
    setIsDialogOpen(false);
    setFirstName(member.firstName ?? "");
    setLastName(member.lastName ?? "");
    setEmail(member.email);
    setIsEmailVerified(member.emailVerified);
    setShouldShowEmailChangeDialog(false);
  }

  /** Whether there are any changes in member details dialog */
  const hasNoChanges =
    firstName.trim() === (member.firstName ?? "").trim() &&
    lastName.trim() === (member.lastName ?? "").trim() &&
    email === member.email &&
    isEmailVerified === member.emailVerified;

  function checkValidity(): {
    isFirstNameValid: boolean;
    isLastNameValid: boolean;
    isEmailValid: boolean;
  } {
    return {
      isFirstNameValid: firstName.trim().length > 0,
      isLastNameValid: lastName.trim().length > 0,
      isEmailValid: isValidEmail(email),
    };
  }

  async function onUpdateMemberDetails(): Promise<void> {
    if (email !== member.email && shouldShowEmailChangeDialog === false) {
      setShouldShowEmailChangeDialog(true);
      return;
    }

    setShouldShowEmailChangeDialog(false);

    trackEvent({
      name: MemberEvents.editMemberDetails,
    });

    try {
      await dispatch(
        updateMember({
          userId: member.identity,
          firstName,
          lastName,
          email,
          isEmailVerified,
        })
      ).unwrap();
      openToast({
        title: "Member details updated successfully!",
      });
      setIsDialogOpen(false);
    } catch (error) {
      handleErrorWithToast({
        id: `UpdateMember-${Date.now().toString()}`,
        title: "Could not update member details",
        error,
      });
    }
  }

  return (
    <>
      <RequirePermission permission="member.edit">
        <Button
          onClick={() => {
            trackEvent({
              name: MemberEvents.openEditMemberDetailsDialog,
            });
            setIsDialogOpen(true);
          }}
          variant="secondary"
        >
          Edit Member Details
        </Button>
      </RequirePermission>

      <Dialog
        open={isDialogOpen}
        title={
          <TextWithTooltip
            prefix="Edit"
            text={getMemberDisplayName(member)}
            suffix="details"
          />
        }
        onCancel={onCloseDialog}
        onConfirm={onUpdateMemberDetails}
        isConfirmDisabled={
          Object.values(checkValidity()).some((isValid) => !isValid) ||
          hasNoChanges
        }
        confirmTooltip={hasNoChanges ? "No changes to save" : undefined}
        showSpinner={fetchingMember.updatingMember === "pending"}
      >
        <TextField
          label="User Identity"
          text={member.identity}
          disabled={true}
          onTextChanged={() => {}}
          fullWidth
          controlStyle={{ mt: 2 }}
        />

        <TextField
          label="First name"
          text={firstName}
          onTextChanged={setFirstName}
          error={!checkValidity().isFirstNameValid && "Minimum 1 character"}
          fullWidth
          controlStyle={{ mt: 2 }}
        />

        <TextField
          label="Last name"
          text={lastName}
          onTextChanged={setLastName}
          error={!checkValidity().isLastNameValid && "Minimum 1 character"}
          fullWidth
          controlStyle={{ mt: 2 }}
        />

        <TextField
          label="Email"
          text={email}
          onTextChanged={setEmail}
          error={
            !checkValidity().isEmailValid &&
            "Please enter a valid email address"
          }
          fullWidth
          controlStyle={{ mt: 2 }}
        />

        <Switch
          label="Is email verified?"
          checked={isEmailVerified}
          onToggle={() => setIsEmailVerified(!isEmailVerified)}
          sx={{ mt: 2, ml: "0px" }}
        />
      </Dialog>

      <NotificationDialog
        notificationType="warning"
        open={shouldShowEmailChangeDialog}
        title="Are you sure you also want to change the email address?"
        onCancel={() => setShouldShowEmailChangeDialog(false)}
        onConfirm={onUpdateMemberDetails}
        showSpinner={fetchingMember.updatingMember === "pending"}
      >
        <Text variant="bodyL">
          When changing the email address, it's your responsibility to check
          that the person making the change request is authorized to do so and
          they are the owner of the new email address.
        </Text>
        <TextField
          label="Old email address"
          text={member.email}
          disabled={true}
          onTextChanged={() => {}}
          fullWidth
          controlStyle={{ mt: 2 }}
        />
        <TextField
          label="New email address"
          text={email}
          disabled={true}
          onTextChanged={() => {}}
          fullWidth
          controlStyle={{ mt: 2 }}
        />
      </NotificationDialog>
    </>
  );
}
